import React, { useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import Header from './Header';
import Footer from './Footer';
import { useI18n } from '../i18n/I18nContext';
import { getAdminCopy } from '../i18n/adminCopy';
import { authErrorMessage } from '../services/auth';
import { requestPasswordReset } from '../services/passwordReset';
import fallbackTellerImage from '../assets/images/tarot-empress.jpg';
import '../style/AdminPanel.css';

const ADMIN_EMAIL = 'pe.lipovsky@gmail.com';

const emptyCounts = {
  users: 0,
  tellers: 0,
  sessions: 0,
  bookings: 0,
};

const ADMIN_SECTIONS = [
  { id: 'sessions', index: '01', label: 'Sessions', description: 'Availability, dates and session status' },
  { id: 'bookings', index: '02', label: 'Bookings', description: 'Payments, cancellations and clients' },
  { id: 'users', index: '03', label: 'Users', description: 'Client accounts and access' },
  { id: 'tellers', index: '04', label: 'Reader applications', description: 'Provider review and public profiles' },
  { id: 'pro', index: '05', label: 'Pro requests', description: 'Membership payment requests' },
];

const sanitizePayload = (payload) =>
  Object.entries(payload).reduce((clean, [key, value]) => {
    if (value !== undefined) {
      clean[key] = value;
    }
    return clean;
  }, {});

const toNumberOrUndefined = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSessionDuration = (value) => {
  const normalized = String(value || '1').toLowerCase();
  if (normalized.includes('2 hour') || normalized === '120') return '2 hours';
  if (normalized.includes('1 hour') || normalized === '60') return '1 hour';
  const duration = Number(value || 1);
  if (duration === 120) return '2 hours';
  if (duration === 60) return '1 hour';
  return Number.isFinite(duration) ? `${duration} ${duration === 1 ? 'hour' : 'hours'}` : '1 hour';
};

const sessionStatus = (session) => {
  if (session.cancelled) return 'Cancelled';
  if (session.completed) return 'Completed';
  if (session.cancelRequest) return 'Cancel requested';
  if (session.booked) return `Booked${session.bookedBy ? ` by ${session.bookedBy}` : ''}`;
  return 'Available';
};

const bookingStatus = (booking) => {
  if (booking.refunded) return 'Refunded';
  if (booking.cancelAccepted) return 'Cancellation accepted';
  if (booking.cancelRequest) return 'Cancellation pending';
  if (booking.cancelledByTeller) return 'Cancelled by reader';
  if (booking.completed) return 'Completed';
  if (booking.paid) return 'Paid';
  return 'Booked';
};

const tellerStatus = (teller) => {
  if (teller.banned) return 'banned';
  return teller.approvalStatus || (teller.approved ? 'approved' : 'draft');
};

const tellerStatusLabel = (status) => ({
  draft: 'Draft',
  under_review: 'Under review',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  rejected: 'Rejected',
  banned: 'Suspended',
}[status] || status.replaceAll('_', ' '));

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const recordDateValue = (record) => {
  const scheduled = record.startTime && String(record.startTime).includes('T')
    ? record.startTime
    : `${record.date || ''}T${record.startTime || record.time || '00:00'}`;
  const scheduledTime = timestampValue(scheduled);
  return scheduledTime || timestampValue(record.updatedAt) || timestampValue(record.createdAt);
};

const AdminPanel = () => {
  const { language } = useI18n();
  const adminCopy = getAdminCopy(language);
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [firestoreError, setFirestoreError] = useState('');
  const [savingId, setSavingId] = useState('');
  const [pendingDelete, setPendingDelete] = useState('');
  const [inlineNotice, setInlineNotice] = useState('');
  const [reviewFeedback, setReviewFeedback] = useState({});
  const [activeSection, setActiveSection] = useState('sessions');

  const [users, setUsers] = useState([]);
  const [tellers, setTellers] = useState([]);
  const [tellerApplications, setTellerApplications] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [proOrders, setProOrders] = useState([]);

  const [sessionEdits, setSessionEdits] = useState({});
  const [bookingEdits, setBookingEdits] = useState({});
  const [userEdits, setUserEdits] = useState({});
  const [tellerEdits, setTellerEdits] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setAuthLoading(false);

      if (!currentUser) {
        setAdminUser(null);
        return;
      }

      if ((currentUser.email || '').toLowerCase() !== ADMIN_EMAIL) {
        await signOut(auth);
        setAdminUser(null);
        setAuthError(adminCopy.restricted);
        return;
      }

      setAuthError('');
      setAdminUser(currentUser);
    });

    return () => unsubscribe();
  }, [adminCopy.restricted]);

  useEffect(() => {
    if (!adminUser) return undefined;

    setFirestoreError('');
    const handleError = (error) => {
      console.error('Admin Firestore error:', error);
      setFirestoreError(
        `${error.message}. Deploy firestore.rules from this repo so ${ADMIN_EMAIL} has admin read/write access.`
      );
    };

    const unsubscribers = [
      onSnapshot(collection(db, 'users'), (snapshot) => {
        setUsers(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      }, handleError),
      onSnapshot(collection(db, 'fortuneTellers'), (snapshot) => {
        setTellers(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      }, handleError),
      onSnapshot(collection(db, 'tellerApplications'), (snapshot) => {
        setTellerApplications(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      }, handleError),
      onSnapshot(collection(db, 'availableSessions'), (snapshot) => {
        setSessions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      }, handleError),
      onSnapshot(collection(db, 'bookings'), (snapshot) => {
        setBookings(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      }, handleError),
      onSnapshot(collection(db, 'proOrders'), (snapshot) => {
        setProOrders(snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))));
      }, handleError),
    ];

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [adminUser]);

  const reviewTellers = useMemo(() => {
    const publicTellersById = new Map(tellers.map((item) => [item.id, item]));
    const applicationsById = new Map(tellerApplications.map((item) => [item.id, item]));
    const legacyTellerUsers = users.filter((item) => (
      item.role === 'teller'
      || item.type === 'fortuneTeller'
      || item.role === 'fortuneTeller'
    ));
    const ids = new Set([
      ...publicTellersById.keys(),
      ...applicationsById.keys(),
      ...legacyTellerUsers.map((item) => item.id),
    ]);

    return [...ids].map((id) => {
      const account = legacyTellerUsers.find((item) => item.id === id) || {};
      const publicProfile = publicTellersById.get(id) || {};
      const application = applicationsById.get(id) || {};
      return {
        ...account,
        ...application,
        // The reviewed public profile is authoritative for approval state.
        // Private application data still fills fields that are not public.
        ...publicProfile,
        id,
        userId: id,
        role: 'teller',
        type: 'fortuneTeller',
        __hasPublicProfile: publicTellersById.has(id),
      };
    });
  }, [tellerApplications, tellers, users]);

  const pendingTellerReviews = useMemo(
    () => reviewTellers.filter((teller) => tellerStatus(teller) === 'under_review').length,
    [reviewTellers],
  );

  const adminSessions = useMemo(() => {
    const existingSessionIds = new Set(sessions.map((session) => session.id));
    const bookingOnlySessions = bookings
      .filter((booking) => booking.sessionId && !existingSessionIds.has(booking.sessionId))
      .map((booking) => ({
        id: booking.sessionId,
        sessionName: booking.sessionName || 'Booked session',
        description: booking.description || 'Session created from booking record.',
        date: booking.date || '',
        startTime: booking.startTime || '',
        duration: booking.duration || 1,
        price: booking.price || 0,
        booked: true,
        bookedBy: booking.bookedBy || booking.userEmail || booking.userId || '',
        completed: Boolean(booking.completed),
        cancelled: Boolean(booking.cancelAccepted || booking.cancelledByTeller),
        cancelRequest: Boolean(booking.cancelRequest),
        tutorEmail: booking.tutorEmail || '',
        tutorName: booking.tutorName || '',
        __source: 'booking',
        __bookingId: booking.id,
      }));

    return [
      ...sessions.map((session) => ({ ...session, __source: 'availableSessions' })),
      ...bookingOnlySessions,
    ].sort((a, b) => `${b.date || ''}T${b.startTime || ''}`.localeCompare(`${a.date || ''}T${a.startTime || ''}`));
  }, [sessions, bookings]);

  const orderedBookings = useMemo(
    () => [...bookings].sort((a, b) => recordDateValue(b) - recordDateValue(a)),
    [bookings],
  );

  const orderedUsers = useMemo(
    () => [...users].sort((a, b) => (
      timestampValue(b.updatedAt) || timestampValue(b.createdAt)
    ) - (
      timestampValue(a.updatedAt) || timestampValue(a.createdAt)
    )),
    [users],
  );

  const counts = useMemo(() => {
    if (!adminUser) return emptyCounts;

    return {
      users: users.length,
      tellers: tellers.length,
      sessions: adminSessions.length,
      bookings: bookings.length,
    };
  }, [adminUser, users, tellers, adminSessions, bookings]);

  const bookingsBySessionId = useMemo(() => {
    return bookings.reduce((map, booking) => {
      if (!booking.sessionId) return map;
      map[booking.sessionId] = map[booking.sessionId] || [];
      map[booking.sessionId].push(booking);
      return map;
    }, {});
  }, [bookings]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setAuthError('');
    setAuthNotice('');

    if (loginEmail.trim().toLowerCase() !== ADMIN_EMAIL) {
      setAuthError(`${adminCopy.wrongAccount} ${ADMIN_EMAIL}.`);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), password);
      setPassword('');
    } catch (error) {
      console.error('Admin login error:', error);
      setAuthError(authErrorMessage(error, language));
    }
  };

  const handlePasswordReset = async () => {
    setAuthError('');
    setAuthNotice('');
    try {
      await requestPasswordReset(ADMIN_EMAIL);
      setAuthNotice('A password reset link was sent to the approved admin email address.');
    } catch (error) {
      console.error('Admin password reset error:', error);
      setAuthError(authErrorMessage(error, language));
    }
  };

  const updateSessionDraft = (sessionId, field, value) => {
    setSessionEdits((current) => ({
      ...current,
      [sessionId]: {
        ...current[sessionId],
        [field]: value,
      },
    }));
  };

  const updateBookingDraft = (bookingId, field, value) => {
    setBookingEdits((current) => ({
      ...current,
      [bookingId]: {
        ...current[bookingId],
        [field]: value,
      },
    }));
  };

  const updateUserDraft = (userId, field, value) => {
    setUserEdits((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        [field]: value,
      },
    }));
  };

  const updateTellerDraft = (tellerId, field, value) => {
    setTellerEdits((current) => ({
      ...current,
      [tellerId]: {
        ...current[tellerId],
        [field]: value,
      },
    }));
  };

  const saveSession = async (session) => {
    const draft = sessionEdits[session.id] || {};
    setSavingId(`session-${session.id}`);

    try {
      await updateDoc(doc(db, 'availableSessions', session.id), sanitizePayload({
        sessionName: draft.sessionName,
        description: draft.description,
        date: draft.date,
        startTime: draft.startTime,
        duration: toNumberOrUndefined(draft.duration),
        price: toNumberOrUndefined(draft.price),
        bookedBy: draft.bookedBy,
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      }));

      setSessionEdits((current) => ({ ...current, [session.id]: {} }));
    } finally {
      setSavingId('');
    }
  };

  const saveBooking = async (booking) => {
    const draft = bookingEdits[booking.id] || {};
    setSavingId(`booking-${booking.id}`);

    try {
      await updateDoc(doc(db, 'bookings', booking.id), sanitizePayload({
        date: draft.date,
        startTime: draft.startTime,
        duration: toNumberOrUndefined(draft.duration),
        price: toNumberOrUndefined(draft.price),
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      }));

      if (booking.sessionId && (draft.date || draft.startTime || draft.duration || draft.price)) {
        await updateDoc(doc(db, 'availableSessions', booking.sessionId), sanitizePayload({
          date: draft.date,
          startTime: draft.startTime,
          duration: toNumberOrUndefined(draft.duration),
          price: toNumberOrUndefined(draft.price),
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        }));
      }

      setBookingEdits((current) => ({ ...current, [booking.id]: {} }));
    } finally {
      setSavingId('');
    }
  };

  const saveUser = async (userProfile) => {
    const draft = userEdits[userProfile.id] || {};
    setSavingId(`user-${userProfile.id}`);

    try {
      await updateDoc(doc(db, 'users', userProfile.id), sanitizePayload({
        name: draft.name,
        email: draft.email,
        type: draft.type,
        disabled: draft.disabled,
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      }));

      setUserEdits((current) => ({ ...current, [userProfile.id]: {} }));
    } finally {
      setSavingId('');
    }
  };

  const processProOrder = async (order, approved) => {
    if (!order?.accountId) return;
    setSavingId(`pro-${order.id}`);

    try {
      const batch = writeBatch(db);
      const orderRef = doc(db, 'proOrders', order.id);
      const userRef = doc(db, 'users', order.accountId);
      const isCancellation = order.requestType === 'cancel-pro';

      if (!approved) {
        batch.update(orderRef, {
          status: 'cancelled',
          processedAt: serverTimestamp(),
          processedBy: ADMIN_EMAIL,
        });
      } else if (isCancellation) {
        batch.update(userRef, {
          [order.planField || 'tellerSubscriptionPlan']: 'free',
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
        batch.update(orderRef, {
          status: 'cancelled',
          cancellationApproved: true,
          processedAt: serverTimestamp(),
          processedBy: ADMIN_EMAIL,
        });

        if (order.targetOrderId) {
          const targetRef = doc(db, 'proOrders', order.targetOrderId);
          const targetSnapshot = await getDoc(targetRef);
          if (targetSnapshot.exists()) {
            batch.update(targetRef, {
              status: 'cancelled',
              cancelledAt: serverTimestamp(),
              processedBy: ADMIN_EMAIL,
            });
          }
        }
      } else {
        batch.update(userRef, {
          [order.planField || 'tellerSubscriptionPlan']: 'pro',
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
        batch.update(orderRef, {
          status: 'paid',
          paidAt: serverTimestamp(),
          processedAt: serverTimestamp(),
          processedBy: ADMIN_EMAIL,
        });
      }

      await batch.commit();
    } catch (error) {
      setFirestoreError(error.message || 'The Pro request could not be processed.');
    } finally {
      setSavingId('');
    }
  };

  const saveTeller = async (teller) => {
    const draft = tellerEdits[teller.id] || {};
    setSavingId(`teller-${teller.id}`);

    try {
      await setDoc(doc(db, 'fortuneTellers', teller.id), sanitizePayload({
        userId: teller.id,
        role: 'teller',
        type: 'fortuneTeller',
        name: draft.name,
        email: draft.email,
        tutorEmail: draft.tutorEmail,
        description: draft.description,
        aboutme: draft.aboutme,
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      }), { merge: true });

      setTellerEdits((current) => ({ ...current, [teller.id]: {} }));
    } finally {
      setSavingId('');
    }
  };

  const reviewTeller = async (teller, decision) => {
    const allowedDecisions = ['approved', 'changes_requested', 'rejected', 'banned', 'under_review'];
    if (!allowedDecisions.includes(decision)) return;

    const currentStatus = tellerStatus(teller);
    const nextStatus = decision === 'banned' ? 'banned' : decision;
    if (currentStatus === nextStatus) {
      const unchangedMessage = `This application is already ${tellerStatusLabel(currentStatus).toLowerCase()}. No new notification was sent.`;
      setInlineNotice(unchangedMessage);
      setReviewFeedback((current) => ({
        ...current,
        [teller.id]: { type: 'warning', message: unchangedMessage },
      }));
      return;
    }

    const draft = tellerEdits[teller.id] || {};
    const note = String(draft.reviewNote ?? teller.reviewNote ?? '').trim();
    if (['changes_requested', 'rejected'].includes(decision) && note.length < 10) {
      const validationMessage = 'Add a review note of at least 10 characters before requesting changes or rejecting an application.';
      setInlineNotice(validationMessage);
      setReviewFeedback((current) => ({
        ...current,
        [teller.id]: { type: 'error', message: validationMessage },
      }));
      return;
    }

    setSavingId(`review-${teller.id}`);
    setInlineNotice('');
    setFirestoreError('');
    setReviewFeedback((current) => ({
      ...current,
      [teller.id]: { type: 'pending', message: 'Saving review decision...' },
    }));
    const status = decision === 'banned' ? 'banned' : decision;
    const approved = decision === 'approved';
    const reviewedTeller = { ...teller, ...draft };
    const titleByDecision = {
      approved: 'Your reader application was approved',
      changes_requested: 'Your reader application needs changes',
      rejected: 'Your reader application was not approved',
      banned: 'Your reader profile was suspended',
      under_review: 'Your reader application is under review',
    };
    const bodyByDecision = {
      approved: 'Your public profile is active. You can now publish availability and accept sessions.',
      changes_requested: note,
      rejected: note,
      banned: note || 'Your public profile and availability have been suspended. Contact support for details.',
      under_review: 'The Fate Room team is reviewing your application.',
    };

    try {
      const publicProfilePayload = {
        iban: deleteField(),
        bankAccount: deleteField(),
        userId: teller.id,
        role: 'teller',
        type: 'fortuneTeller',
        name: reviewedTeller.name || reviewedTeller.displayName || reviewedTeller.email || 'Fate Room reader',
        email: reviewedTeller.email || reviewedTeller.tutorEmail || '',
        tutorEmail: reviewedTeller.tutorEmail || reviewedTeller.email || '',
        description: reviewedTeller.description || reviewedTeller.bio || reviewedTeller.aboutme || '',
        bio: reviewedTeller.bio || reviewedTeller.description || reviewedTeller.aboutme || '',
        profileImage: reviewedTeller.profileImage || reviewedTeller.photoUrl || '',
        photoUrl: reviewedTeller.photoUrl || reviewedTeller.profileImage || '',
        skills: reviewedTeller.skills || reviewedTeller.specializations || reviewedTeller.category || [],
        category: reviewedTeller.category || reviewedTeller.specializations || reviewedTeller.skills || [],
        languages: reviewedTeller.languages || [],
        price: Number(reviewedTeller.price || 30),
        approvalStatus: status,
        approved,
        banned: decision === 'banned',
        reviewedAt: serverTimestamp(),
        reviewedBy: ADMIN_EMAIL,
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      };

      // Make the marketplace profile authoritative first. Secondary records
      // and delivery services must never roll back an admin decision.
      await setDoc(doc(db, 'fortuneTellers', teller.id), publicProfilePayload, { merge: true });
      setTellers((current) => {
        const existing = current.find((item) => item.id === teller.id);
        const nextProfile = {
          ...(existing || {}),
          ...publicProfilePayload,
          id: teller.id,
          reviewedAt: new Date(),
          updatedAt: new Date(),
        };
        return existing
          ? current.map((item) => (item.id === teller.id ? nextProfile : item))
          : [...current, nextProfile];
      });

      const applicationSync = setDoc(doc(db, 'tellerApplications', teller.id), {
        userId: teller.id,
        email: reviewedTeller.email || reviewedTeller.tutorEmail || '',
        approvalStatus: status,
        reviewNote: note,
        reviewedAt: serverTimestamp(),
        reviewedBy: ADMIN_EMAIL,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Approval is the critical operation. Notifications are best-effort so
      // an email delivery issue cannot roll back the teller review decision.
      const deliveryTasks = [applicationSync];
      const notificationRef = doc(collection(db, 'notifications'));
      deliveryTasks.push(setDoc(notificationRef, {
        recipientId: teller.id,
        recipientRole: 'teller',
        userId: teller.id,
        actorId: adminUser.uid,
        type: `teller_application_${status}`,
        title: titleByDecision[decision],
        body: bodyByDecision[decision],
        actionUrl: decision === 'approved' ? '/teller-dashboard' : '/onboarding/teller',
        read: false,
        createdAt: serverTimestamp(),
      }));

      const recipient = String(reviewedTeller.email || reviewedTeller.tutorEmail || '').trim();
      if (recipient) {
        const mailRef = doc(collection(db, 'mail'));
        deliveryTasks.push(setDoc(mailRef, {
          to: recipient,
          message: {
            subject: titleByDecision[decision],
            text: `${bodyByDecision[decision]}\n\nOpen Fate Room: ${window.location.origin}${decision === 'approved' ? '/teller-dashboard' : '/onboarding/teller'}`,
          },
          createdAt: serverTimestamp(),
          type: `teller_application_${status}`,
        }));
      }

      const deliveryResults = await Promise.allSettled(deliveryTasks);
      const deliveryFailed = deliveryResults.some((result) => result.status === 'rejected');
      setTellerEdits((current) => ({ ...current, [teller.id]: {} }));
      const successMessage = `${reviewedTeller.name || reviewedTeller.email || 'Reader'}: ${titleByDecision[decision]}.`;
      setInlineNotice(successMessage);
      setReviewFeedback((current) => ({
        ...current,
        [teller.id]: {
          type: deliveryFailed ? 'warning' : 'success',
          message: deliveryFailed
            ? `${successMessage} The profile was updated, but one notification could not be delivered.`
            : successMessage,
        },
      }));
    } catch (reviewError) {
      const message = reviewError.message || 'The reader review decision could not be saved.';
      setFirestoreError(message);
      setReviewFeedback((current) => ({
        ...current,
        [teller.id]: { type: 'error', message: `Review was not saved: ${message}` },
      }));
    } finally {
      setSavingId('');
    }
  };

  const setSessionState = async (session, state) => {
    setSavingId(`session-state-${session.id}`);
    try {
      const sessionRef = doc(db, 'availableSessions', session.id);
      const batch = writeBatch(db);

      if (state === 'completed') {
        batch.update(sessionRef, {
          completed: true,
          cancelled: false,
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      if (state === 'available') {
        batch.update(sessionRef, {
          booked: false,
          bookedBy: '',
          completed: false,
          cancelled: false,
          cancelRequest: false,
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      if (state === 'cancelled') {
        batch.update(sessionRef, {
          booked: false,
          cancelled: true,
          cancelRequest: false,
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      (bookingsBySessionId[session.id] || []).forEach((booking) => {
        const bookingRef = doc(db, 'bookings', booking.id);
        if (state === 'completed') {
          batch.update(bookingRef, {
            completed: true,
            cancelRequest: false,
            cancelledByTeller: false,
            status: 'completed',
            updatedAt: serverTimestamp(),
            updatedByAdmin: ADMIN_EMAIL,
          });
        }
        if (state === 'available') {
          batch.update(bookingRef, {
            cancelRequest: false,
            cancelAccepted: true,
            cancelledByTeller: true,
            status: 'cancelled',
            updatedAt: serverTimestamp(),
            updatedByAdmin: ADMIN_EMAIL,
          });
        }
        if (state === 'cancelled') {
          batch.update(bookingRef, {
            cancelRequest: false,
            cancelAccepted: true,
            cancelledByTeller: true,
            status: 'cancelled',
            updatedAt: serverTimestamp(),
            updatedByAdmin: ADMIN_EMAIL,
          });
        }
      });

      await batch.commit();
    } finally {
      setSavingId('');
    }
  };

  const acceptCancellation = async (booking) => {
    setSavingId(`accept-${booking.id}`);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'bookings', booking.id), {
        cancelRequest: false,
        cancelAccepted: true,
        cancelledByTeller: false,
        status: 'cancelled',
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      });

      if (booking.sessionId) {
        batch.update(doc(db, 'availableSessions', booking.sessionId), {
          booked: false,
          bookedBy: '',
          cancelRequest: false,
          cancelled: false,
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      await batch.commit();
    } finally {
      setSavingId('');
    }
  };

  const rejectCancellation = async (booking) => {
    setSavingId(`reject-${booking.id}`);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'bookings', booking.id), {
        cancelRequest: false,
        cancelAccepted: false,
        cancellationDeclined: true,
        status: 'booked',
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      });

      if (booking.sessionId) {
        batch.update(doc(db, 'availableSessions', booking.sessionId), {
          cancelRequest: false,
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      await batch.commit();
    } finally {
      setSavingId('');
    }
  };

  const markBookingPaid = async (booking) => {
    setSavingId(`paid-${booking.id}`);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'bookings', booking.id), {
        paid: true,
        status: 'paid',
        updatedAt: serverTimestamp(),
        updatedByAdmin: ADMIN_EMAIL,
      });

      if (booking.sessionId) {
        batch.update(doc(db, 'availableSessions', booking.sessionId), {
          booked: true,
          bookedBy: booking.bookedBy || booking.userEmail || '',
          updatedAt: serverTimestamp(),
          updatedByAdmin: ADMIN_EMAIL,
        });
      }

      await batch.commit();
    } finally {
      setSavingId('');
    }
  };

  const deleteSession = async (sessionId) => {
    setSavingId(`delete-session-${sessionId}`);
    setInlineNotice('');
    try {
      await deleteDoc(doc(db, 'availableSessions', sessionId));
      setPendingDelete('');
      setInlineNotice('Session deleted permanently.');
    } catch (error) {
      setInlineNotice(`Session could not be deleted: ${error.message}`);
    } finally {
      setSavingId('');
    }
  };

  const deleteBooking = async (bookingId) => {
    setSavingId(`delete-booking-${bookingId}`);
    setInlineNotice('');
    try {
      await deleteDoc(doc(db, 'bookings', bookingId));
      setPendingDelete('');
      setInlineNotice('Booking deleted permanently.');
    } catch (error) {
      setInlineNotice(`Booking could not be deleted: ${error.message}`);
    } finally {
      setSavingId('');
    }
  };

  const renderLogin = () => (
    <>
      <Header />
      <main className="admin-shell admin-login-shell">
        <section className="admin-login-card">
          <p className="admin-eyebrow">{adminCopy.eyebrow}</p>
          <h1>{adminCopy.title}</h1>
          <p>
            {adminCopy.body}
          </p>

          {authError && <div className="admin-alert">{authError}</div>}
          {authNotice && <div className="admin-alert admin-alert-notice">{authNotice}</div>}

          <form className="admin-login-form" onSubmit={handleLogin}>
            <label>
              {adminCopy.email}
              <input
                type="email"
                value={loginEmail}
                onChange={(event) => setLoginEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label>
              {adminCopy.password}
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="admin-primary-button">
              {adminCopy.login}
            </button>
            <button type="button" className="admin-login-reset" onClick={handlePasswordReset}>
              Forgot password?
            </button>
          </form>
        </section>
      </main>
      <Footer />
    </>
  );

  if (authLoading) {
    return (
      <>
        <Header />
        <main className="admin-shell">
          <div className="admin-card">{adminCopy.checking}</div>
        </main>
        <Footer />
      </>
    );
  }

  if (!adminUser) {
    return renderLogin();
  }

  const activeSectionMeta = ADMIN_SECTIONS.find((section) => section.id === activeSection) || ADMIN_SECTIONS[0];
  const sectionCounts = {
    sessions: counts.sessions,
    bookings: counts.bookings,
    users: counts.users,
    tellers: pendingTellerReviews,
    pro: proOrders.filter((order) => order.status === 'pending').length,
  };

  return (
    <>
      <Header />
      <main className="admin-shell">
        <div className="admin-workspace">
          <aside className="admin-sidebar">
            <div className="admin-sidebar-brand">
              <span className="admin-sidebar-mark">FR</span>
              <div>
                <strong>Fate Room</strong>
                <span>Owner workspace</span>
              </div>
            </div>

            <nav className="admin-tabs" aria-label="Admin sections">
              {ADMIN_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  className={activeSection === section.id ? 'active' : ''}
                  onClick={() => setActiveSection(section.id)}
                  type="button"
                >
                  <span className="admin-tab-index">{section.index}</span>
                  <span className="admin-tab-copy">
                    <strong>{section.label}</strong>
                    <small>{section.description}</small>
                  </span>
                  <span className="admin-tab-count">{sectionCounts[section.id]}</span>
                </button>
              ))}
            </nav>

            <div className="admin-sidebar-account">
              <span className="admin-online-dot" aria-hidden="true" />
              <div>
                <strong>Administrator</strong>
                <small>{adminUser.email}</small>
              </div>
              <button type="button" onClick={() => signOut(auth)}>Sign out</button>
            </div>
          </aside>

          <div className="admin-main-column">
            <header className="admin-command-bar">
              <div>
                <p className="admin-eyebrow">Admin control panel</p>
                <h1>{activeSectionMeta.label}</h1>
                <p>{activeSectionMeta.description}. Changes are saved directly to the live marketplace.</p>
              </div>
              <div className="admin-command-summary">
                <span>Signed in</span>
                <strong>{adminUser.email}</strong>
              </div>
            </header>

            {firestoreError && <div className="admin-alert admin-alert-error">{firestoreError}</div>}
            {inlineNotice && <div className="admin-alert admin-alert-notice" role="status">{inlineNotice}</div>}

            <section className="admin-stats-grid" aria-label="Marketplace overview">
              <div className="admin-stat-card"><span>Users</span><strong>{counts.users}</strong></div>
              <div className="admin-stat-card"><span>Readers</span><strong>{counts.tellers}</strong></div>
              <div className="admin-stat-card"><span>Reviews</span><strong>{pendingTellerReviews}</strong></div>
              <div className="admin-stat-card"><span>Sessions</span><strong>{counts.sessions}</strong></div>
              <div className="admin-stat-card"><span>Bookings</span><strong>{counts.bookings}</strong></div>
            </section>

            <div className="admin-content-area">

        {activeSection === 'sessions' && (
          <section className="admin-card admin-sessions-card">
            <div className="admin-section-heading admin-sessions-heading">
              <div>
                <span className="admin-section-kicker">Schedule management</span>
                <h2>Sessions</h2>
              </div>
              <p>Edit reader availability, move dates, complete, cancel, reopen, or delete sessions. Booking-only sessions are shown here too; edit those from the Bookings tab.</p>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table admin-sessions-table">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Date / time</th>
                    <th>Duration</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Booked by</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminSessions.map((session) => {
                    const draft = sessionEdits[session.id] || {};

                    if (session.__source === 'booking') {
                      return (
                        <tr key={session.id} className="admin-booking-session-row">
                          <td colSpan="7">
                            <div className="admin-booking-session-summary">
                              <div className="admin-booking-session-intro">
                                <span className="admin-booking-session-kicker">Booking-only session</span>
                                <strong>{session.sessionName || 'Reading session'}</strong>
                                <p>{session.description || 'Session created from a booking record.'}</p>
                                <details className="admin-session-meta">
                                  <summary>Technical details</summary>
                                  <span>Session ID: {session.id}</span>
                                  <span>Source: booking fallback</span>
                                </details>
                              </div>
                              <dl className="admin-booking-session-facts">
                                <div>
                                  <dt>Date and time</dt>
                                  <dd>{[session.date, session.startTime].filter(Boolean).join(' at ') || 'Not scheduled'}</dd>
                                </div>
                                <div>
                                  <dt>Duration</dt>
                                  <dd>{formatSessionDuration(session.duration)}</dd>
                                </div>
                                <div>
                                  <dt>Price</dt>
                                  <dd>€{formatMoney(session.price)}</dd>
                                </div>
                                <div>
                                  <dt>Status</dt>
                                  <dd><span className="admin-status-pill">{sessionStatus(session)}</span></dd>
                                </div>
                                <div>
                                  <dt>Booked by</dt>
                                  <dd>{session.bookedBy || session.userEmail || 'Not assigned'}</dd>
                                </div>
                              </dl>
                              <div className="admin-booking-session-action">
                                <strong>Managed as a booking</strong>
                                <span>Use the Bookings tab to edit payment, cancellation, client details, or session status.</span>
                                <button type="button" onClick={() => setActiveSection('bookings')}>
                                  Open bookings
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={session.id}>
                        <td className="admin-session-main-cell" data-label="Session">
                          <div className="admin-field-stack">
                            <span className="admin-field-label">Session name</span>
                            <input
                              value={draft.sessionName ?? session.sessionName ?? ''}
                              onChange={(event) => updateSessionDraft(session.id, 'sessionName', event.target.value)}
                              aria-label="Session name"
                            />
                            <span className="admin-field-label">Description</span>
                            <textarea
                              value={draft.description ?? session.description ?? ''}
                              onChange={(event) => updateSessionDraft(session.id, 'description', event.target.value)}
                              aria-label="Session description"
                            />
                          </div>
                          <details className="admin-session-meta">
                            <summary>Technical details</summary>
                            <span>Session ID: {session.id}</span>
                            <span>Source: {session.__source === 'booking' ? 'booking fallback' : 'availableSessions'}</span>
                          </details>
                        </td>
                        <td data-label="Date / time">
                          <div className="admin-date-time-grid">
                            <label>
                              <span>Date</span>
                              <input
                                type="date"
                                value={draft.date ?? session.date ?? ''}
                                onChange={(event) => updateSessionDraft(session.id, 'date', event.target.value)}
                                aria-label="Session date"
                              />
                            </label>
                            <label>
                              <span>Start time</span>
                              <input
                                type="time"
                                value={draft.startTime ?? session.startTime ?? ''}
                                onChange={(event) => updateSessionDraft(session.id, 'startTime', event.target.value)}
                                aria-label="Session start time"
                              />
                            </label>
                          </div>
                        </td>
                        <td data-label="Duration">
                          <select
                            value={draft.duration ?? session.duration ?? 1}
                            onChange={(event) => updateSessionDraft(session.id, 'duration', event.target.value)}
                            aria-label="Session duration"
                          >
                            <option value="1">1 hour</option>
                            <option value="2">2 hours</option>
                            <option value="60">60 min</option>
                            <option value="120">120 min</option>
                          </select>
                        </td>
                        <td data-label="Price">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.price ?? session.price ?? ''}
                            onChange={(event) => updateSessionDraft(session.id, 'price', event.target.value)}
                            aria-label="Session price"
                          />
                        </td>
                        <td data-label="Status">
                          <span className="admin-status-pill">{sessionStatus(session)}</span>
                        </td>
                        <td data-label="Booked by">
                          <input
                            value={draft.bookedBy ?? session.bookedBy ?? ''}
                            onChange={(event) => updateSessionDraft(session.id, 'bookedBy', event.target.value)}
                            aria-label="Booked by"
                          />
                        </td>
                        <td data-label="Actions">
                          <div className="admin-action-stack admin-session-actions">
                              <button type="button" className="admin-session-save" onClick={() => saveSession(session)} disabled={savingId === `session-${session.id}`}>
                                Save changes
                              </button>
                              <div className="admin-session-action-grid">
                                <button type="button" onClick={() => setSessionState(session, 'completed')}>
                                  Complete
                                </button>
                                <button type="button" onClick={() => setSessionState(session, 'available')}>
                                  Reopen
                                </button>
                                <button type="button" onClick={() => setSessionState(session, 'cancelled')}>
                                  Cancel
                                </button>
                              </div>
                              {pendingDelete === `session-${session.id}` ? (
                                <div className="admin-delete-confirmation">
                                  <span className="admin-inline-note">Delete permanently?</span>
                                  <button type="button" className="danger" onClick={() => deleteSession(session.id)} disabled={savingId === `delete-session-${session.id}`}>
                                    {savingId === `delete-session-${session.id}` ? 'Deleting...' : 'Confirm delete'}
                                  </button>
                                  <button type="button" onClick={() => setPendingDelete('')}>Keep session</button>
                                </div>
                              ) : (
                                <button type="button" className="danger admin-session-delete" onClick={() => {
                                  setPendingDelete(`session-${session.id}`);
                                  setInlineNotice('Confirm permanent deletion directly in the selected session row.');
                                }}>
                                  Delete
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {adminSessions.length === 0 && (
                    <tr>
                      <td colSpan="7">No sessions found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSection === 'bookings' && (
          <section className="admin-card">
            <div className="admin-section-heading">
              <h2>Bookings</h2>
              <p>Move booked sessions, accept or reject cancellation requests, mark payment, or complete bookings.</p>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Booking</th>
                    <th>User / reader</th>
                    <th>Date / time</th>
                    <th>Duration</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedBookings.map((booking) => {
                    const draft = bookingEdits[booking.id] || {};
                    return (
                      <tr key={booking.id}>
                        <td data-label="Booking">
                          <strong>{booking.sessionName || 'Session'}</strong>
                          <small>Booking: {booking.id}</small>
                          <small>Session: {booking.sessionId || '-'}</small>
                        </td>
                        <td data-label="User / reader">
                          <small>User: {booking.bookedBy || booking.userId || '-'}</small>
                          <small>Reader: {booking.tutorEmail || booking.tutorName || '-'}</small>
                        </td>
                        <td data-label="Date / time">
                          <input
                            type="date"
                            value={draft.date ?? booking.date ?? ''}
                            onChange={(event) => updateBookingDraft(booking.id, 'date', event.target.value)}
                            aria-label="Booking date"
                          />
                          <input
                            type="time"
                            value={draft.startTime ?? booking.startTime ?? ''}
                            onChange={(event) => updateBookingDraft(booking.id, 'startTime', event.target.value)}
                            aria-label="Booking start time"
                          />
                        </td>
                        <td data-label="Duration">
                          <select
                            value={draft.duration ?? booking.duration ?? 1}
                            onChange={(event) => updateBookingDraft(booking.id, 'duration', event.target.value)}
                            aria-label="Booking duration"
                          >
                            <option value="1">1 hour</option>
                            <option value="2">2 hours</option>
                            <option value="60">60 min</option>
                            <option value="120">120 min</option>
                          </select>
                        </td>
                        <td data-label="Price">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.price ?? booking.price ?? ''}
                            onChange={(event) => updateBookingDraft(booking.id, 'price', event.target.value)}
                            aria-label="Booking price"
                          />
                        </td>
                        <td data-label="Status">
                          <span className="admin-status-pill">{bookingStatus(booking)}</span>
                        </td>
                        <td data-label="Actions">
                          <div className="admin-action-stack">
                            <button onClick={() => saveBooking(booking)} disabled={savingId === `booking-${booking.id}`}>
                              Save
                            </button>
                            <button onClick={() => acceptCancellation(booking)}>
                              Accept cancel
                            </button>
                            <button onClick={() => rejectCancellation(booking)}>
                              Reject cancel
                            </button>
                            <button onClick={() => markBookingPaid(booking)}>
                              Mark paid
                            </button>
                            <button onClick={() => setSessionState({ id: booking.sessionId }, 'completed')} disabled={!booking.sessionId}>
                              Complete
                            </button>
                            {pendingDelete === `booking-${booking.id}` ? (
                              <>
                                <span className="admin-inline-note">Delete permanently?</span>
                                <button className="danger" onClick={() => deleteBooking(booking.id)} disabled={savingId === `delete-booking-${booking.id}`}>
                                  {savingId === `delete-booking-${booking.id}` ? 'Deleting...' : 'Confirm delete'}
                                </button>
                                <button onClick={() => setPendingDelete('')}>Keep</button>
                              </>
                            ) : (
                              <button className="danger" onClick={() => {
                                setPendingDelete(`booking-${booking.id}`);
                                setInlineNotice('Confirm permanent deletion directly in the selected booking row.');
                              }}>
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {bookings.length === 0 && (
                    <tr>
                      <td colSpan="7">No bookings found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSection === 'users' && (
          <section className="admin-card">
            <div className="admin-section-heading">
              <h2>Users</h2>
              <p>Edit client profiles, account type, and disabled flag.</p>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Disabled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedUsers.map((userProfile) => {
                    const draft = userEdits[userProfile.id] || {};
                    return (
                      <tr key={userProfile.id}>
                        <td data-label="Name">
                          <input
                            value={draft.name ?? userProfile.name ?? ''}
                            onChange={(event) => updateUserDraft(userProfile.id, 'name', event.target.value)}
                            aria-label="User name"
                          />
                          <small>{userProfile.id}</small>
                        </td>
                        <td data-label="Email">
                          <input
                            type="email"
                            value={draft.email ?? userProfile.email ?? ''}
                            onChange={(event) => updateUserDraft(userProfile.id, 'email', event.target.value)}
                            aria-label="User email"
                          />
                        </td>
                        <td data-label="Type">
                          <select
                            value={draft.type ?? userProfile.type ?? 'user'}
                            onChange={(event) => updateUserDraft(userProfile.id, 'type', event.target.value)}
                            aria-label="User type"
                          >
                            <option value="user">User</option>
                            <option value="fortuneTeller">Reader</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td data-label="Disabled">
                          <label className="admin-checkbox">
                            <input
                              type="checkbox"
                              checked={draft.disabled ?? Boolean(userProfile.disabled)}
                              onChange={(event) => updateUserDraft(userProfile.id, 'disabled', event.target.checked)}
                            />
                            Disabled
                          </label>
                        </td>
                        <td data-label="Actions">
                          <button onClick={() => saveUser(userProfile)} disabled={savingId === `user-${userProfile.id}`}>
                            Save user
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="5">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSection === 'tellers' && (
          <section className="admin-card admin-teller-applications-card">
            <div className="admin-section-heading admin-teller-applications-heading">
              <div>
                <span className="admin-section-kicker">Provider onboarding</span>
                <h2>Reader applications</h2>
              </div>
              <p>Review identity, experience, presentation and responsible-reading standards before a profile becomes public.</p>
            </div>
            <div className="admin-teller-review-list">
              {[...reviewTellers]
                .sort((a, b) => {
                  const priority = { under_review: 0, changes_requested: 1, draft: 2, approved: 3, rejected: 4, banned: 5 };
                  const statusOrder = (priority[tellerStatus(a)] ?? 9) - (priority[tellerStatus(b)] ?? 9);
                  if (statusOrder !== 0) return statusOrder;
                  return (
                    timestampValue(b.submittedAt) || timestampValue(b.updatedAt) || timestampValue(b.createdAt)
                  ) - (
                    timestampValue(a.submittedAt) || timestampValue(a.updatedAt) || timestampValue(a.createdAt)
                  );
                })
                .map((teller) => {
                  const draft = tellerEdits[teller.id] || {};
                  const status = tellerStatus(teller);
                  const reviewBusy = savingId === `review-${teller.id}`;
                  const reviewedAt = timestampValue(teller.reviewedAt);
                  return (
                    <article className="admin-teller-review-card" key={teller.id}>
                      <header className="admin-teller-review-header">
                        <div className="admin-teller-identity">
                          <img src={teller.profileImage || teller.photoUrl || fallbackTellerImage} alt="" />
                          <div className="admin-teller-identity-copy">
                            <span className={`admin-status-pill ${status}`}>{status.replaceAll('_', ' ')}</span>
                            <h3>{teller.name || 'Unnamed reader'}</h3>
                            <p>{teller.headline || 'No profile headline yet'}</p>
                            <small>{teller.email || teller.tutorEmail || teller.id}</small>
                          </div>
                        </div>
                        <div className="admin-teller-header-actions">
                          <span className="admin-application-id">Application {teller.id.slice(0, 8)}</span>
                          {teller.introVideoUrl && <a href={teller.introVideoUrl} target="_blank" rel="noreferrer">Introduction video</a>}
                        </div>
                      </header>

                      <div className="admin-teller-evidence">
                        <div><span>Specialties</span><strong>{(teller.skills || teller.category || []).join(', ') || 'Not provided'}</strong></div>
                        <div><span>Languages</span><strong>{(teller.languages || []).join(', ') || 'Not provided'}</strong></div>
                        <div><span>Experience</span><strong>{teller.experience ?? 0} years</strong></div>
                        <div><span>Starting price</span><strong>€{formatMoney(teller.price)}</strong></div>
                        <div><span>Country</span><strong>{teller.country || 'Not provided'}</strong></div>
                        <div className={teller.identityConfirmed ? 'is-confirmed' : 'needs-review'}>
                          <span>Identity confirmed</span>
                          <strong>{teller.identityConfirmed ? 'Yes' : 'Needs review'}</strong>
                        </div>
                      </div>

                      <div className="admin-teller-description">
                        <div>
                          <span className="admin-content-label">Public introduction</span>
                          <p>{teller.description || teller.bio || 'No introduction provided.'}</p>
                        </div>
                        <div>
                          <span className="admin-content-label">Certifications or training</span>
                          <p>
                            {(Array.isArray(teller.certifications)
                              ? teller.certifications.join(', ')
                              : teller.certifications) || 'No training information provided.'}
                          </p>
                        </div>
                      </div>

                      <div className="admin-teller-editor">
                        <div className="admin-teller-profile-editor">
                          <div className="admin-editor-heading">
                            <span>Edit public profile</span>
                            <small>Changes here update the information clients will see after approval.</small>
                          </div>
                          <div className="admin-teller-profile-fields">
                            <label>
                              Public name
                              <input
                                value={draft.name ?? teller.name ?? ''}
                                onChange={(event) => updateTellerDraft(teller.id, 'name', event.target.value)}
                                aria-label="Reader name"
                              />
                            </label>
                            <label>
                              Account email
                              <input
                                type="email"
                                value={draft.email ?? teller.email ?? ''}
                                onChange={(event) => updateTellerDraft(teller.id, 'email', event.target.value)}
                                aria-label="Reader email"
                              />
                            </label>
                            <label>
                              Booking email
                              <input
                                type="email"
                                value={draft.tutorEmail ?? teller.tutorEmail ?? teller.email ?? ''}
                                onChange={(event) => updateTellerDraft(teller.id, 'tutorEmail', event.target.value)}
                                aria-label="Reader booking email"
                              />
                            </label>
                            <label className="admin-teller-description-field">
                              Public description
                              <textarea
                                value={draft.description ?? teller.description ?? teller.aboutme ?? ''}
                                onChange={(event) => {
                                  updateTellerDraft(teller.id, 'description', event.target.value);
                                  updateTellerDraft(teller.id, 'aboutme', event.target.value);
                                }}
                                aria-label="Reader description"
                              />
                            </label>
                          </div>
                          <button type="button" className="admin-save-profile-button" onClick={() => saveTeller(teller)} disabled={savingId === `teller-${teller.id}`}>
                            {savingId === `teller-${teller.id}` ? 'Saving profile...' : 'Save profile edits'}
                          </button>
                        </div>

                        <div className="admin-teller-decision">
                          <div className="admin-editor-heading">
                            <span>Application decision</span>
                            <small>Leave a clear note before requesting changes or rejecting an application.</small>
                          </div>
                          <div className={`admin-current-decision ${status}`}>
                            <span>Current decision</span>
                            <strong>{tellerStatusLabel(status)}</strong>
                            <small>
                              {reviewedAt
                                ? `Saved ${new Date(reviewedAt).toLocaleString('en-GB')} by ${teller.reviewedBy || ADMIN_EMAIL}`
                                : 'No final review decision has been recorded yet.'}
                            </small>
                          </div>
                          <label>
                            Review note
                            <textarea
                              value={draft.reviewNote ?? teller.reviewNote ?? ''}
                              onChange={(event) => updateTellerDraft(teller.id, 'reviewNote', event.target.value)}
                              placeholder="Explain requested changes or the reason for rejection..."
                            />
                          </label>
                          <div className="admin-review-actions">
                            <button type="button" className={`success ${status === 'approved' ? 'is-current' : ''}`} onClick={() => reviewTeller(teller, 'approved')} disabled={reviewBusy || status === 'approved'}>
                              {status === 'approved' ? 'Approved' : 'Approve'}
                            </button>
                            <button type="button" className={status === 'changes_requested' ? 'is-current' : ''} onClick={() => reviewTeller(teller, 'changes_requested')} disabled={reviewBusy || status === 'changes_requested'}>
                              {status === 'changes_requested' ? 'Changes requested' : 'Request changes'}
                            </button>
                            <button type="button" className={`danger ${status === 'rejected' ? 'is-current' : ''}`} onClick={() => reviewTeller(teller, 'rejected')} disabled={reviewBusy || status === 'rejected'}>
                              {status === 'rejected' ? 'Rejected' : 'Reject'}
                            </button>
                            {status === 'banned' ? (
                              <button type="button" onClick={() => reviewTeller(teller, 'under_review')} disabled={reviewBusy}>
                                Return to review
                              </button>
                            ) : (
                              <button type="button" className="danger subtle" onClick={() => reviewTeller(teller, 'banned')} disabled={reviewBusy}>
                                Suspend
                              </button>
                            )}
                          </div>
                          {reviewFeedback[teller.id] && (
                            <p
                              className={`admin-review-feedback ${reviewFeedback[teller.id].type}`}
                              role="status"
                              aria-live="polite"
                            >
                              {reviewFeedback[teller.id].message}
                            </p>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              {reviewTellers.length === 0 && <div className="admin-empty-state">No reader applications found.</div>}
            </div>
          </section>
        )}

        {activeSection === 'pro' && (
          <section className="admin-card">
            <div className="admin-section-heading">
              <h2>Pro requests</h2>
              <p>Confirm Client and Reader Pro payments, activate memberships, or process cancellation requests.</p>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Request</th><th>Account</th><th>Amount</th><th>Status</th><th>Created</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {proOrders.map((order) => {
                    const cancellation = order.requestType === 'cancel-pro';
                    const membershipLabel = order.planField === 'subscriptionPlan' ? 'Client Pro' : 'Reader Pro';
                    return (
                      <tr key={order.id}>
                        <td data-label="Request">
                          <strong>{cancellation ? `Cancel ${membershipLabel}` : `Activate ${membershipLabel}`}</strong>
                          <small>{order.id}</small>
                        </td>
                        <td data-label="Account"><span>{order.email || order.accountId}</span><small>{order.accountId}</small></td>
                        <td data-label="Amount">€{formatMoney(order.amountEUR)}</td>
                        <td data-label="Status"><span className="admin-status-pill">{order.status || 'pending'}</span></td>
                        <td data-label="Created">{order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</td>
                        <td data-label="Actions">
                          <div className="admin-action-stack">
                            {order.status === 'pending' ? (
                              <>
                                <button onClick={() => processProOrder(order, true)} disabled={savingId === `pro-${order.id}`}>
                                  {cancellation ? 'Confirm cancellation' : 'Confirm payment'}
                                </button>
                                <button className="danger" onClick={() => processProOrder(order, false)} disabled={savingId === `pro-${order.id}`}>
                                  Reject
                                </button>
                              </>
                            ) : <span className="admin-inline-note">Processed</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {proOrders.length === 0 && <tr><td colSpan="6">No Pro requests found.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="admin-card admin-note">
          <div>
            <span className="admin-note-eyebrow">Managed activity</span>
            <h2>Marketplace overview</h2>
          </div>
          <p>
            Review booking, payment, cancellation, and session details before changing a record.
            Every update is reflected in the corresponding client or reader dashboard.
          </p>
          <p className="admin-note-value">
            <strong>€{formatMoney(adminSessions.reduce((sum, session) => sum + Number(session.price || 0), 0))}</strong>
            <span>Total managed session value</span>
          </p>
        </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AdminPanel;
